/**
 * ProductsStep — Stage 2 of RAO cart flow.
 *
 * Wave 1 #2 port from SPFx ProductsScreen.tsx (Bill 2026-04-27 lock):
 *   - Category tabs (DDA / SAV / CD / IRA / BOX) at the top with in-cart badges
 *   - Cart-constraint hide-filter — products whose constraints fail against
 *     the cart's people are hidden entirely (NOT grayed out) once at least
 *     one person is in the cart. Constraints checked: businessOnly,
 *     personalOnly, customerTypes, MinAgePrimary/MaxAgePrimary,
 *     MinAgeCoOwner/MaxAgeCoOwner, requiresGuardian.
 *   - BUSINESS_TYPES / PERSONAL_TYPES derive from
 *     `config.entityRules.businessOnly` / `personalOnly` flags (no
 *     hardcoded list — admin-config'd per project_admin_configurable).
 *   - Preserves existing v3 extensions: opening deposits, beneficiary
 *     picker, debit card design panel.
 *
 * Persistence (row-store P2, blueprint 2026-07-06): per-INSTANCE line-item
 * commands — `carts.addLineItem` / `updateLineItem` / `removeLineItem` /
 * `setLineItemBeneficiaries`. The server recomposes productsJson +
 * metadataJson after every command, so legacy readers stay correct. Two
 * instances of the same product code are now independent lines keyed by
 * `instanceId` ("li-{uuid}"; legacy backfill: instanceId == productCode).
 * Emits SessionEvents per product toggle.
 */
import { useMemo, useState, useEffect } from 'react';
import type { CartDto, CartSelectedProduct } from '@retail-ops/shared-types';
import type { Product } from '@retail-ops/shared-types/config';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import { Banner, Button, ModalShell } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import type { ProductDefinition } from '@/contexts/ConfigContext';
import { ProductCard } from '../components/ProductCard';
import {
  AddToCartModal,
  type AddToCartCommit,
  type CartPersonForModal,
  type CardDesignOption,
} from '../components/AddToCartModal';
import type { BenefDraft } from '../components/BeneficiaryEditorModal';
import {
  ProductCategoryPickerModal,
  type ProductCategoryKey,
} from '../components/ProductCategoryPickerModal';
import { ProductTilesPickerModal } from '../components/ProductTilesPickerModal';
import { DISCONTINUED_PRODUCT_CODES } from '../constants';
import { useApi } from '../hooks/useApi';
import { useEmitEvent } from '../hooks/useEmitEvent';
import { normalizeCustomerType } from '../utils/normalizeCustomerType';
import { SETTING_ENABLED_ENTITY_GROUPS } from '@/utils/rolloutScope';
import { enabledEntityTypes, productInRolloutScope } from '../rolloutProducts';

// ── Cart person shape (parsed from cart.peopleJson) ──────────────────────
interface CartPerson {
  id?: string;
  customerType?: CustomerType | string;
  dob?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  taxId?: string;
  cifId?: string;
  role?: string; // 'primary' | 'joint' | 'beneficiary' | etc.
  /** Disambiguates the derived 'NonCitizen' type for product eligibility. */
  citizenshipStatus?: string;
  /** Gates overdraft protection (D4): OD requires an employed owner. */
  employmentStatus?: string;
}

function parsePeopleArray(cart: CartDto): CartPerson[] {
  if (!cart.peopleJson) return [];
  try {
    const raw = JSON.parse(cart.peopleJson) as unknown;
    let arr: Array<Record<string, unknown>> = [];
    if (Array.isArray(raw)) arr = raw as Array<Record<string, unknown>>;
    else if (raw && typeof raw === 'object' && Array.isArray((raw as { people?: unknown }).people)) {
      arr = (raw as { people: Array<Record<string, unknown>> }).people;
    }
    return arr
      .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
      .map((p) => ({
        id: typeof p['id'] === 'string' ? p['id'] : undefined,
        // Normalize JHA short codes ('I' / 'B') → long-form CustomerType
        // ('Individual' / 'Corporation') so product whitelists + age gates
        // match. Existing carts saved with short codes are healed at read.
        customerType: typeof p['customerType'] === 'string'
          ? normalizeCustomerType(p['customerType'])
          : undefined,
        dob: typeof p['dob'] === 'string' ? p['dob'] : undefined,
        firstName: typeof p['firstName'] === 'string' ? p['firstName'] : undefined,
        middleName: typeof p['middleName'] === 'string' ? p['middleName'] : undefined,
        lastName: typeof p['lastName'] === 'string' ? p['lastName'] : undefined,
        taxId: typeof p['taxId'] === 'string' ? p['taxId'] : undefined,
        cifId: typeof p['cifId'] === 'string' ? p['cifId'] : undefined,
        role: typeof p['role'] === 'string' ? p['role'] : undefined,
        citizenshipStatus:
          typeof p['citizenshipStatus'] === 'string' ? p['citizenshipStatus'] : undefined,
        employmentStatus:
          typeof p['employmentStatus'] === 'string' ? p['employmentStatus'] : undefined,
      }));
  } catch {
    return [];
  }
}

// (Removed Bill 2026-06-01: getDisabledReason constraint hide-filter + the
// category-tab style helpers (tabBtn/tabBadge) — the legacy category-tab product
// grid was retired in the Screen-25 restoration. Per-product eligibility is now
// enforced inside the picker chain / AddToCartModal; the cart shows only added items.)

export interface ProductsStepProps {
  cart: CartDto;
  onCartUpdated?: (cart: CartDto) => void;
  readOnly?: boolean;
  /**
   * Add a person to the cart (opens the People step's "+ New Person" dialog). Used by the
   * product modal's "+ Add joint owner" when nobody in the cart can be a joint owner
   * (MacKenzie Mott 2026-10-16; Bill approved).
   */
  onAddPerson?: () => void;
}

// Use the canonical `CartSelectedProduct` shape from shared-types so backend
// `CartCommitService` + frontend agree on `cart.productsJson` contract.
// Bill 2026-05-11 — no orphan local types.
// Row-store P2 (2026-07-06): composed lines now ALWAYS carry `instanceId`
// ("li-{uuid}"; legacy backfill sets instanceId == productCode). Kept as a
// local intersection until the shared-types release lands it.
type SelectedProduct = CartSelectedProduct & { instanceId?: string };

/**
 * Stable per-line identity: the row-store instance id, falling back to the
 * product code for legacy carts (backfill guarantees instanceId == code).
 */
function lineKey(row: Pick<SelectedProduct, 'instanceId' | 'code'>): string {
  return row.instanceId ?? row.code;
}

/** New line-item instance id per the row-store wire contract ("li-" + uuid). */
function genLineItemId(): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `li-${uuid}`;
}

function parseProducts(cart: CartDto): SelectedProduct[] {
  if (!cart.productsJson) return [];
  try {
    const raw = JSON.parse(cart.productsJson) as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
      .map((p) => {
        const out: SelectedProduct = {
          code: typeof p['code'] === 'string' ? p['code'] : '',
          qty: typeof p['qty'] === 'number' ? p['qty'] : 1,
        };
        if (typeof p['instanceId'] === 'string' && p['instanceId'].length > 0) {
          out.instanceId = p['instanceId'];
        }
        if (typeof p['cardTypeCode'] === 'string') out.cardTypeCode = p['cardTypeCode'];
        if (typeof p['openingDeposit'] === 'number') out.openingDeposit = p['openingDeposit'];
        if (typeof p['primaryOwnerId'] === 'string') out.primaryOwnerId = p['primaryOwnerId'];
        if (Array.isArray(p['jointOwnerIds'])) {
          out.jointOwnerIds = (p['jointOwnerIds'] as unknown[]).filter((x): x is string => typeof x === 'string');
        }
        if (Array.isArray(p['beneficiaryIds'])) {
          out.beneficiaryIds = (p['beneficiaryIds'] as unknown[]).filter((x): x is string => typeof x === 'string');
        }
        if (p['featureValues'] && typeof p['featureValues'] === 'object') {
          out.featureValues = p['featureValues'] as Record<string, unknown>;
        }
        return out;
      })
      .filter((p) => p.code.length > 0);
  } catch {
    return [];
  }
}

/**
 * Read the per-INSTANCE beneficiary roster from cart.metadataJson.beneficiaries.
 *
 * Row-store P2 (2026-07-06): the map is keyed by line-item INSTANCE ID going
 * forward. Because the legacy backfill set instanceId == productCode, old
 * carts' code-keyed entries are already valid instance keys — we still try
 * the product code as an explicit fallback for maximum tolerance.
 *
 * WRITES no longer happen here: the roster persists via the
 * `setLineItemBeneficiaries` replace-set command and the server recomposes
 * metadataJson (v0.24.1's client-side read-merge writer is retired).
 */
function readBeneficiaryRoster(
  cart: CartDto,
  instanceId: string,
  productCode: string,
): ReadonlyArray<BenefDraft> | undefined {
  if (!cart.metadataJson) return undefined;
  try {
    const meta = JSON.parse(cart.metadataJson) as Record<string, unknown>;
    const map = meta['beneficiaries'];
    if (map && typeof map === 'object' && !Array.isArray(map)) {
      const m = map as Record<string, unknown>;
      const rows = m[instanceId] ?? m[productCode];
      if (Array.isArray(rows)) return rows as ReadonlyArray<BenefDraft>;
    }
  } catch { /* leave undefined */ }
  return undefined;
}

/**
 * Collect beneficiaries entered on OTHER product lines so the FSR can reuse
 * one instead of re-typing it (Bill v0.70.0 walk / BACKLOG-0837: the same
 * "Sample Beneficiary + custodian" had to be entered once per product).
 * Reads the whole cart.metadataJson.beneficiaries map, skips the line being
 * configured, and dedupes by name + tax id / CIF so a beneficiary on three
 * lines shows once. Returns a fresh copy each (no shared references).
 */
function collectReusableBeneficiaries(
  cart: CartDto,
  currentInstanceId: string,
): ReadonlyArray<BenefDraft> {
  if (!cart.metadataJson) return [];
  try {
    const meta = JSON.parse(cart.metadataJson) as Record<string, unknown>;
    const map = meta['beneficiaries'];
    if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
    const out: BenefDraft[] = [];
    const seen = new Set<string>();
    for (const [key, rows] of Object.entries(map as Record<string, unknown>)) {
      if (key === currentInstanceId) continue; // don't offer this line's own roster
      if (!Array.isArray(rows)) continue;
      for (const r of rows as ReadonlyArray<BenefDraft>) {
        if (!r || typeof r !== 'object') continue;
        const name = `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim().toLowerCase();
        if (!name) continue;
        const dedup = `${name}|${(r.taxId ?? r.cifId ?? '').toLowerCase()}`;
        if (seen.has(dedup)) continue;
        seen.add(dedup);
        out.push({ ...r });
      }
    }
    return out;
  } catch {
    return [];
  }
}

/** Emoji for product acctType — mirrors ProductCard's acctTypeEmoji. */
function productEmoji(acctType: string): string {
  switch (acctType) {
    case '10': return '💳';
    case '20': return '💰';
    case '30': return '📈';
    case '40': return '📝';
    case '50': return '🔒';
    case '60': return '🏦';
    case '91': return '🏧';
    default:   return '📦';
  }
}

export function ProductsStep({ cart, onCartUpdated, readOnly = false, onAddPerson }: ProductsStepProps): JSX.Element {
  const { config } = useConfig();
  const { client, makeSignal } = useApi();
  const emit = useEmitEvent();

  const [selection, setSelection] = useState<SelectedProduct[]>(() => parseProducts(cart));
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Bill 2026-05-08 — modal-state replaces the old flat-section approach.
  // Row-store P2 (2026-07-06): the modal now targets a specific line-item
  // INSTANCE, not a product code. 'add' pre-generates the "li-{uuid}" id so
  // a second instance of the same code never hydrates (or overwrites) the
  // first; 'edit' re-opens an existing instance by its id.
  const [modalTarget, setModalTarget] = useState<{
    mode: 'add' | 'edit';
    productCode: string;
    instanceId: string;
  } | null>(null);

  // Bill 2026-05-13 — Screen 22/23 3-modal product picker chain.
  // `pickerStep` drives the chain: 'closed' → 'category' → 'tiles' → AddToCartModal.
  // The CategoryPicker stays simple — we don't need its own product-code state
  // because the chain hands off an add-mode modalTarget (with a freshly
  // generated line-item instanceId) directly.
  const [pickerStep, setPickerStep] = useState<'closed' | 'category' | 'tiles'>('closed');
  const [pickerCategoryKey, setPickerCategoryKey] = useState<ProductCategoryKey | null>(null);
  // Pending cart-line removal awaiting FSR confirmation. window.confirm() is a
  // silent no-op inside the Teams webview (Bill 2026-07-17: "Remove product
  // button non-functional"), so the confirm gate is an in-app ModalShell.
  const [pendingRemoval, setPendingRemoval] = useState<{ instanceId: string; label: string; row: SelectedProduct } | null>(null);

  // Re-sync if the parent reloads the cart from server.
  useEffect(() => {
    setSelection(parseProducts(cart));
  }, [cart.cartId, cart.version]);

  // Wave 1 #2 — derive BUSINESS_TYPES / PERSONAL_TYPES from EntityRules
  // (admin-config'd, not hardcoded). SPFx canonical hardcoded these — Bill
  // 2026-04-27 locked admin-config derivation.
  const { businessTypes, personalTypes } = useMemo(() => {
    const biz = new Set<string>();
    const personal = new Set<string>();
    for (const r of (config.entityRules ?? [])) {
      if (!r.isActive) continue;
      if (r.businessOnly) biz.add(r.customerType);
      if (r.personalOnly) personal.add(r.customerType);
    }
    return { businessTypes: biz, personalTypes: personal };
  }, [config.entityRules]);

  // Cart's people (used by getDisabledReason for constraint hide-filter).
  const people = useMemo(() => parsePeopleArray(cart), [cart.peopleJson]);

  // Bill 2026-05-12 (SPEC Screen 25 line 2416) — per-product ownership chips.
  // Pre-build O(1) lookups keyed by personId and product code so the render
  // loop can format chips without scanning arrays per card.
  const peopleById = useMemo(() => {
    const m = new Map<string, CartPerson>();
    for (const p of people) {
      if (p.id) m.set(p.id, p);
    }
    return m;
  }, [people]);
  const personDisplayName = (id: string | undefined): string | undefined => {
    if (!id) return undefined;
    const p = peopleById.get(id);
    if (!p) return undefined;
    const full = `${p.firstName?.trim() ?? ''} ${p.lastName?.trim() ?? ''}`.trim();
    return full || undefined;
  };

  // Filter out discontinued + inactive products. Constraint hide-filter
  // applies later, per-active-category, so a constraint violation only
  // hides the offending card (not the whole category tab).
  const activeProducts = useMemo(
    () =>
      (config.products ?? []).filter(
        (p) => p.isActive && !DISCONTINUED_PRODUCT_CODES.includes(p.code),
      ),
    [config.products],
  );

  // visibleProducts — drives the count text + the no-products empty state.
  // (The legacy category-tab grouping was removed with the Screen-25 restoration;
  // the 3-modal picker chain is now the sole add path — Bill 2026-06-01.)
  // Phase rollout scope (Bill 2026-10-06): count only products offered in this phase.
  const visibleProducts = useMemo(() => {
    const types = enabledEntityTypes((config.systemSettings as Record<string, unknown> | undefined)?.[SETTING_ENABLED_ENTITY_GROUPS]);
    return activeProducts.filter((p) => productInRolloutScope(p, types));
  }, [activeProducts, config.systemSettings]);

  // Build productCode -> featureCode[] index (feeds each cart-item card's chips).
  const featuresByProduct = useMemo(() => {
    const m = new Map<string, string[]>();
    (config.productFeatures ?? []).forEach((pf) => {
      const list = m.get(pf.productCode) ?? [];
      list.push(pf.featureCode);
      m.set(pf.productCode, list);
    });
    return m;
  }, [config.productFeatures]);

  // Row-store P2 command runner (blueprint 2026-07-06). Each command returns
  // the FULL recomposed cart ({ success, cart }); the LAST successful cart
  // propagates to the parent so the re-sync effect re-parses productsJson.
  // Commands never send a cart-level expectedVersion — the server serializes
  // per-row, so the old "single update call per expectedVersion" constraint
  // is gone. On failure we surface the plain-language error and fall back to
  // the last known-good cart state (dropping the optimistic selection).
  const runCommands = async (
    commands: ReadonlyArray<(signal: AbortSignal) => Promise<{
      success: boolean;
      cart?: CartDto;
      error?: string;
    }>>,
  ): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    const signal = makeSignal();
    try {
      let latest: CartDto | null = null;
      for (const command of commands) {
        const res = await command(signal);
        if (!res.success || !res.cart) {
          setSaveError(res.error ?? 'Save failed');
          if (latest) onCartUpdated?.(latest);
          else setSelection(parseProducts(cart));
          return;
        }
        latest = res.cart;
      }
      if (latest) onCartUpdated?.(latest);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
      setSelection(parseProducts(cart));
    } finally {
      setSaving(false);
    }
  };

  // In-cart card click — open the config modal for THIS instance, or remove it.
  // Bill 2026-05-08 lock: per-product binding happens in AddToCartModal,
  // NOT in flat sections below the grid (which v0.14.23 strips).
  const onCartItemToggle = (row: SelectedProduct, next: boolean): void => {
    if (readOnly) return;
    const instanceId = lineKey(row);
    if (next) {
      // Re-opening for edit: the actual line item is persisted on modal
      // commit, not here. Targets the clicked INSTANCE (not the code) so two
      // lines of the same product open independently.
      setModalTarget({ mode: 'edit', productCode: row.code, instanceId });
      return;
    }
    // next === false → remove from cart. Per SPEC Screen 25:2429 — confirm first
    // (no instant delete). window.confirm() no-ops in the Teams webview, so open
    // an in-app ModalShell confirm (rendered below); confirmRemoval() does the work.
    const removedDef = activeProducts.find((p) => p.code === row.code);
    const removedLabel = removedDef?.name ?? row.code;
    setPendingRemoval({ instanceId, label: removedLabel, row });
  };

  // Executed when the FSR confirms removal in the ModalShell. The removal is
  // audit-logged via the cart.product.removed emit; the server drops the line's
  // beneficiary roster and recomposes productsJson + metadataJson.
  const confirmRemoval = (): void => {
    const pending = pendingRemoval;
    if (!pending) return;
    setPendingRemoval(null);
    const { instanceId, row } = pending;
    const nextSelection = selection.filter((s) => lineKey(s) !== instanceId);
    setSelection(nextSelection);
    emit({
      eventType: 'cart.product.removed',
      eventCategory: 'cart-lifecycle',
      cartId: cart.cartId,
      payload: { productCode: row.code, instanceId },
    });
    void runCommands([
      (signal) => client.retail.carts.removeLineItem(cart.cartId, instanceId, { signal }),
    ]);
  };

  // Modal commit — per-INSTANCE line-item command (row-store P2).
  // NEW add → POST /line-items with the pre-generated instanceId (ALWAYS
  // creates a new line — never upserts by code). Edit → PATCH by instanceId.
  const onCommitModal = (commit: AddToCartCommit): void => {
    if (readOnly) return;
    const target = modalTarget;
    if (!target) return;
    const productCode = commit.product.code;
    const instanceId = commit.instanceId ?? target.instanceId;
    const existingIdx = selection.findIndex((s) => lineKey(s) === instanceId);
    // Tolerate a stale edit target (line vanished under us, e.g. a server
    // re-sync removed it): fall through to add so the commit isn't lost.
    const isEdit = target.mode === 'edit' && existingIdx >= 0;
    const qty = isEdit ? (selection[existingIdx]?.qty ?? 1) : 1;

    const merged: SelectedProduct = {
      code: productCode,
      instanceId,
      qty,
      primaryOwnerId: commit.primaryOwnerId,
      jointOwnerIds: commit.jointOwnerIds,
      beneficiaryIds: commit.beneficiaryIds,
      featureValues: commit.featureValues,
      ...(commit.openingDeposit !== undefined ? { openingDeposit: commit.openingDeposit } : {}),
      ...(commit.cardTypeCode ? { cardTypeCode: commit.cardTypeCode } : {}),
    };
    const nextSelection = isEdit
      ? selection.map((s, i) => (i === existingIdx ? merged : s))
      : [...selection, merged];
    setSelection(nextSelection);
    setModalTarget(null);
    emit({
      eventType: isEdit ? 'cart.product.updated' : 'cart.product.added',
      eventCategory: 'cart-lifecycle',
      cartId: cart.cartId,
      payload: {
        productCode,
        instanceId,
        primaryOwnerId: commit.primaryOwnerId,
        jointCount: commit.jointOwnerIds.length,
        beneficiaryCount: commit.beneficiaries.length,
      },
    });

    // Wire contract: config = the SelectedProduct object minus
    // code / instanceId / primaryOwnerId / jointOwnerIds (owners travel as
    // top-level command fields; the server recomposes the composed line).
    const config: Record<string, unknown> = {
      qty,
      beneficiaryIds: [...commit.beneficiaryIds],
      featureValues: commit.featureValues,
      ...(commit.openingDeposit !== undefined ? { openingDeposit: commit.openingDeposit } : {}),
      ...(commit.cardTypeCode ? { cardTypeCode: commit.cardTypeCode } : {}),
    };

    const commands: Array<(signal: AbortSignal) => Promise<{
      success: boolean;
      cart?: CartDto;
      error?: string;
    }>> = [];
    if (isEdit) {
      commands.push((signal) => client.retail.carts.updateLineItem(
        cart.cartId,
        instanceId,
        {
          config,
          primaryOwnerClientId: commit.primaryOwnerId,
          jointOwnerClientIds: [...commit.jointOwnerIds],
        },
        { signal },
      ));
    } else {
      commands.push((signal) => client.retail.carts.addLineItem(
        cart.cartId,
        {
          instanceId,
          productCode,
          config,
          primaryOwnerClientId: commit.primaryOwnerId,
          jointOwnerClientIds: [...commit.jointOwnerIds],
        },
        { signal },
      ));
    }
    // Replace-set the roster keyed by INSTANCE ID. Skip the extra round trip
    // when there's nothing to write AND nothing previously stored to clear.
    const prevRoster = readBeneficiaryRoster(cart, instanceId, productCode);
    if (commit.beneficiaries.length > 0 || (prevRoster && prevRoster.length > 0)) {
      // BenefDraft is an interface (no index signature) — widen to the wire's
      // ReadonlyArray<Record<string, unknown>> entries shape.
      const entries = commit.beneficiaries as unknown as ReadonlyArray<Record<string, unknown>>;
      commands.push((signal) => client.retail.carts.setLineItemBeneficiaries(
        cart.cartId,
        instanceId,
        entries,
        { signal },
      ));
    }
    void runCommands(commands);
  };

  // Modal-input shape: re-derive on each render so the people list reflects
  // any cart edits made after the cart was loaded. Maps CartPerson → modal's
  // CartPersonForModal flat shape (id/customerType/dob/firstName/lastName/cifId).
  const modalPeople: ReadonlyArray<CartPersonForModal> = useMemo(() => {
    return people.map((p) => ({
      id: p.id,
      customerType: p.customerType,
      dob: p.dob,
      firstName: p.firstName,
      middleName: p.middleName,
      lastName: p.lastName,
      taxId: p.taxId,
      cifId: p.cifId,
      // Product eligibility resolves a derived 'NonCitizen' by citizenship class.
      citizenshipStatus: p.citizenshipStatus,
      employmentStatus: p.employmentStatus,
    }));
  }, [people]);

  const modalProduct = useMemo(() => {
    if (!modalTarget) return null;
    return activeProducts.find((p) => p.code === modalTarget.productCode) ?? null;
  }, [modalTarget, activeProducts]);

  // Catalogs the modal needs.
  const modalFeatures = config.features ?? [];
  const modalProductFeatures = useMemo(() => {
    if (!modalTarget) return [];
    return (config.productFeatures ?? []).filter((pf) => pf.productCode === modalTarget.productCode);
  }, [config.productFeatures, modalTarget]);
  // BACKLOG-1668 — Order Checks "Design" picker source. The feature schema (seed 68)
  // points the picker at sourceRef "checkDesigns", but nothing ever supplied it, so
  // the drop-down was empty (Bill 2026-10-01). The catalog is dbo.SystemSettings
  // checks.designs — a JSON array of {code, displayName} — so Deposit Ops can add a
  // design in the Admin Panel without a release. Collette 2026-10-02: safety-paper
  // checks in blue, green and yellow are what the bank orders (seed 254).
  const modalPickerSources = useMemo(() => {
    const raw = (config.systemSettings as Record<string, unknown>)['checks.designs'];
    const list: { code: string; displayName: string }[] = [];
    const push = (item: unknown): void => {
      if (!item || typeof item !== 'object') return;
      const o = item as Record<string, unknown>;
      const code = typeof o['code'] === 'string' ? o['code'].trim() : '';
      const name = [o['displayName'], o['label'], o['name']].find((v) => typeof v === 'string' && (v as string).trim().length > 0) as string | undefined;
      if (code) list.push({ code, displayName: (name ?? code).trim() });
    };
    try {
      const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (Array.isArray(parsed)) parsed.forEach(push);
    } catch { /* malformed setting → empty picker, never a crash */ }
    return { checkDesigns: list };
  }, [config.systemSettings]);
  // FSR-facing notes under a service (BACKLOG-1668): checks.pricingNote — Collette
  // 2026-10-02: about $35 per 60 safety-paper checks; free for customers 60+ on Free Checking.
  const modalFeatureNotes = useMemo(() => {
    const raw = (config.systemSettings as Record<string, unknown>)['checks.pricingNote'];
    const note = typeof raw === 'string' ? raw.trim() : '';
    return note ? { orderChecks: note } : {};
  }, [config.systemSettings]);

  const modalCardTypes: ReadonlyArray<CardDesignOption> = useMemo(() => {
    return (config.cardTypes ?? []).map((c) => {
      // Shared CardType doesn't expose cardCategory today; if the Function
      // route eventually adds it, copy across via untyped lookup so the
      // modal can filter Personal-vs-Business without a shared-types release.
      const anyC = c as unknown as Record<string, unknown>;
      const cardCategory = typeof anyC['cardCategory'] === 'string' ? (anyC['cardCategory'] as string) : undefined;
      // Bill 2026-07-13 r2 / BACKLOG-0873: the real design NAME lives in the
      // CardTypes.Description column ("Rt 66", "Dog", "White Hex"…) and the
      // network in IsoType — the row has no name/cardNetwork, so tiles showed
      // "Code 101". Pull the true fields from the raw row, with fallbacks.
      const description = typeof anyC['description'] === 'string' ? (anyC['description'] as string) : undefined;
      const isoType = typeof anyC['isoType'] === 'string' ? (anyC['isoType'] as string) : undefined;
      const name = c.name || description || `Design ${c.code}`;
      const cardNetwork = c.cardNetwork || isoType;
      // BACKLOG-0873 — the config row carries a relative imagePath
      // (/v3/config/card-designs/{code}/image) when the design has uploaded
      // artwork; resolve it against the API base for an <img>-usable URL.
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
      const imagePath = typeof anyC['imagePath'] === 'string' ? (anyC['imagePath'] as string) : undefined;
      const imageUrl = imagePath ? `${apiBase}${imagePath}` : undefined;
      return {
        code: c.code,
        name,
        ...(cardCategory ? { cardCategory } : {}),
        ...(cardNetwork ? { cardNetwork } : {}),
        ...(imageUrl ? { imageUrl } : {}),
        ...(typeof c.isActive === 'boolean' ? { isActive: c.isActive } : {}),
      };
    });
  }, [config.cardTypes]);

  // Initial state for the modal. ADD mode carries ONLY the pre-generated
  // instanceId — adding the SAME product code again must NOT hydrate an
  // existing line's config (that by-code lookup was the "second product of
  // same type edits the first" regression). EDIT mode hydrates by INSTANCE.
  const modalInitial = useMemo(() => {
    if (!modalTarget) return undefined;
    if (modalTarget.mode === 'add') {
      return { instanceId: modalTarget.instanceId };
    }
    const existing = selection.find((s) => lineKey(s) === modalTarget.instanceId);
    if (!existing) return { instanceId: modalTarget.instanceId };
    // Re-hydrate the full beneficiary roster from
    // metadataJson.beneficiaries[instanceId] (legacy fallback: key == code)
    // so re-opening an edited product shows the rows the FSR entered (the bare
    // beneficiaryIds on the product line don't carry name/%/role/relationship).
    const beneficiaries = readBeneficiaryRoster(cart, modalTarget.instanceId, existing.code);
    return {
      instanceId: modalTarget.instanceId,
      ...(existing.primaryOwnerId ? { primaryOwnerId: existing.primaryOwnerId } : {}),
      ...(existing.jointOwnerIds ? { jointOwnerIds: existing.jointOwnerIds } : {}),
      ...(existing.beneficiaryIds ? { beneficiaryIds: existing.beneficiaryIds } : {}),
      ...(beneficiaries ? { beneficiaries } : {}),
      ...(existing.featureValues ? { featureValues: existing.featureValues } : {}),
      ...(existing.openingDeposit !== undefined ? { openingDeposit: existing.openingDeposit } : {}),
      ...(existing.cardTypeCode ? { cardTypeCode: existing.cardTypeCode } : {}),
    };
  }, [modalTarget, selection, cart.metadataJson]);

  // Screen 25 — product-definition lookup for the in-cart stack.
  const productByCode = useMemo(() => {
    const m = new Map<string, ProductDefinition>();
    for (const p of activeProducts) m.set(p.code, p);
    return m;
  }, [activeProducts]);

  // Row-store P2 — duplicate-code disambiguation. When the SAME product code
  // appears on 2+ lines, each card (and the modal title) shows a minimal
  // "#n" ordinal so the FSR can tell the instances apart. Single-instance
  // codes render exactly as before (no clutter).
  const { codeCounts, ordinalByKey } = useMemo(() => {
    const counts = new Map<string, number>();
    const seen = new Map<string, number>();
    const ordinals = new Map<string, number>();
    for (const s of selection) counts.set(s.code, (counts.get(s.code) ?? 0) + 1);
    for (const s of selection) {
      const n = (seen.get(s.code) ?? 0) + 1;
      seen.set(s.code, n);
      ordinals.set(lineKey(s), n);
    }
    return { codeCounts: counts, ordinalByKey: ordinals };
  }, [selection]);

  // Modal title suffix: edit → this instance's ordinal (only when duplicates
  // exist); add → "#(count+1)" when the code is already in the cart.
  const modalInstanceLabel = useMemo(() => {
    if (!modalTarget) return undefined;
    const count = codeCounts.get(modalTarget.productCode) ?? 0;
    if (modalTarget.mode === 'add') {
      return count >= 1 ? `#${count + 1}` : undefined;
    }
    if (count > 1) {
      const n = ordinalByKey.get(modalTarget.instanceId);
      return n ? `#${n}` : undefined;
    }
    return undefined;
  }, [modalTarget, codeCounts, ordinalByKey]);

  // Screen 25 summary chips (SPEC.md:2418) — Deposit / Services / Card design.
  // Real data only (no dead scaffold); the admin-configurable ProductSummaryChipConfig
  // schema is the follow-on per the SPEC.
  // BACKLOG-1667 (Bill 2026-10-01): "the 'Services' should be listed. There is
  // plenty of room for more detail when in Desktop app." Feature codes resolve to
  // their catalog display names; the compound services say what was chosen.
  const featureLabel = (code: string): string => {
    const cat = (config.features ?? []) as unknown as ReadonlyArray<Record<string, unknown>>;
    const f = cat.find((x) => x['code'] === code);
    const lbl = f ? (f['displayName'] ?? f['label'] ?? f['name']) : undefined;
    return typeof lbl === 'string' && lbl.length > 0 ? lbl : code;
  };
  const buildSummaryChips = (row: SelectedProduct): string[] => {
    const chips: string[] = [];
    if (row.openingDeposit !== undefined && row.openingDeposit !== null) {
      chips.push(`Deposit: $${Number(row.openingDeposit).toLocaleString()}`);
    }
    const fv = row.featureValues ?? {};
    for (const [code, val] of Object.entries(fv)) {
      if (!val) continue;
      if (code === 'debitCardOwners' || code === 'debitCardDesigns' || code === 'debitCard') continue;
      if (code === 'orderChecks' && typeof val === 'object') {
        const o = val as Record<string, unknown>;
        const qty = typeof o['quantity'] === 'number' ? o['quantity'] : undefined;
        const start = o['startNumber'];
        const design = typeof o['design'] === 'string' && o['design'] ? o['design'] : undefined;
        chips.push(['Checks', qty !== undefined ? `${qty}` : undefined, start ? `from #${start}` : undefined, design].filter(Boolean).join(' · '));
        continue;
      }
      if (code === 'overdraftProtection' && typeof val === 'object') {
        const o = val as Record<string, unknown>;
        const lim = o['odLimit'];
        const reg = o['regEOptIn'];
        chips.push(['Overdraft', typeof lim === 'number' ? `$${lim.toLocaleString()}` : undefined,
          reg === 'optIn' ? 'Reg E opted in' : reg === 'optOut' ? 'Reg E declined' : undefined].filter(Boolean).join(' · '));
        continue;
      }
      chips.push(featureLabel(code));
    }
    // Debit cards: who gets one (BACKLOG-1669 — every owner by default on checking).
    const cardOwners = Array.isArray(fv['debitCardOwners'])
      ? (fv['debitCardOwners'] as string[])
      : (fv['debitCard'] || row.cardTypeCode ? [row.primaryOwnerId ?? ''] : []);
    const cardOwnerNames = cardOwners.map((id) => personDisplayName(id)).filter((n): n is string => !!n);
    if (cardOwnerNames.length > 0) chips.push(`Debit cards: ${cardOwnerNames.join(', ')}`);
    else if (row.cardTypeCode || fv['debitCard']) chips.push('Debit card');
    if (row.qty && row.qty > 1) chips.push(`Qty ${row.qty}`);
    return chips;
  };
  // BACKLOG-1667 — POD designation detail for the tile: name · tier · share · relationship.
  const beneficiaryLabels = (row: SelectedProduct): string[] => {
    const roster = readBeneficiaryRoster(cart, row.instanceId ?? row.code, row.code) ?? [];
    return roster.map((b) => {
      const name = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim() || 'Beneficiary';
      const tier = b.role === 'contingent' ? 'Contingent' : 'Primary';
      const pct = typeof b.percentage === 'number' ? `${b.percentage}%` : undefined;
      const rel = b.relationship?.trim() || undefined;
      return [name, tier, pct, rel].filter(Boolean).join(' · ');
    });
  };

  return (
    <section aria-labelledby="rao-products-h">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-5)' }}>
        <div>
          <h2 id="rao-products-h" style={{ fontSize: 18, color: 'var(--pcb-blue-dark)' }}>
            Featured products
          </h2>
          <p style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 'var(--sp-2)' }}>
            {visibleProducts.length === 0
              ? 'Products list is not yet loaded from config.'
              : `${visibleProducts.length} available · ${selection.length} in cart`}
            {saving ? ' · saving…' : ''}
          </p>
        </div>
      </div>

      {saveError && (
        <div style={{ marginTop: 'var(--sp-6)' }}>
          <Banner tone="err">{saveError}</Banner>
        </div>
      )}

      {people.length === 0 && (
        <div style={{
          marginTop: 'var(--sp-6)',
          padding: '12px 16px',
          background: 'var(--pcb-amber-bg, #fef9e7)',
          border: '1px solid var(--pcb-amber, #f3de8a)',
          borderRadius: 'var(--r-sm)',
          fontSize: 13,
          color: 'var(--ink-700)',
        }}>
          Add at least one person on the People tab before selecting products. (Constraints
          like "Personal only" or "Requires age 18+" can't be evaluated until people are added.)
        </div>
      )}

      {/* Bill 2026-05-13 — Screen 22/23 3-modal product picker CTA.
          Opens the CategoryPicker → TilesPicker → AddToCartModal chain.
          Sits above the legacy tabs+grid; ultimately the grid will retire
          once the picker proves out in pilot. */}
      {people.length > 0 && !readOnly && (
        <div className="rao-products__add-cta-row">
          <Button
            variant="primary"
            onClick={() => {
              setPickerCategoryKey(null);
              setPickerStep('category');
            }}
          >
            {selection.length === 0 ? '+ Add product' : '+ Add another product'}
          </Button>
          <span className="rao-products__add-cta-hint">
            Walks you through category → product → owners + services configuration.
          </span>
        </div>
      )}

      {/* ── Screen 25 — "Products in cart" editable card stack (Bill 2026-06-01 restoration).
          Replaces the legacy category-tab grid; the 3-modal picker chain (CTA above) is now
          the SOLE add path. Each card: click re-opens the config modal to edit owners /
          deposit / services; hover-× removes (with confirm). Per SPEC.md Screen 25:2395-2453. ── */}
      {selection.length > 0 && (
        <div className="rao-products__cart-stack" aria-labelledby="rao-cart-stack-h">
          <h3 id="rao-cart-stack-h" className="rao-products__cart-stack-h">Products in cart</h3>
          {selection.map((row) => {
            const def = productByCode.get(row.code);
            if (!def) return null;
            const rowKey = lineKey(row);
            const features = featuresByProduct.get(row.code) ?? [];
            const ownership = {
              primaryName: personDisplayName(row.primaryOwnerId),
              jointNames: (row.jointOwnerIds ?? [])
                .map((id) => personDisplayName(id))
                .filter((n): n is string => !!n),
              beneficiaryCount: (row.beneficiaryIds ?? []).length,
              beneficiaries: beneficiaryLabels(row),
            };
            // Duplicate-code disambiguation: suffix "#n" on the card name only
            // when this code appears on 2+ lines (row-store P2, minimal per
            // Bill's UX rules). ProductCard stays presentation-only.
            const hasDuplicates = (codeCounts.get(row.code) ?? 0) > 1;
            const cardDef = hasDuplicates
              ? { ...def, name: `${def.name} #${ordinalByKey.get(rowKey) ?? '?'}` }
              : def;
            return (
              <ProductCard
                key={rowKey}
                product={cardDef}
                featureCodes={features.map(featureLabel)}
                selected
                cartItem
                disabled={readOnly}
                onToggle={(_code, next) => onCartItemToggle(row, next)}
                ownership={ownership}
                summaryChips={buildSummaryChips(row)}
              />
            );
          })}
        </div>
      )}

      {visibleProducts.length === 0 && (
        <div style={{
          marginTop: 'var(--sp-7)',
          padding: 'var(--sp-10)',
          textAlign: 'center',
          border: '1px dashed var(--ink-300)',
          borderRadius: 'var(--r-md)',
          color: 'var(--ink-600)',
          fontSize: 13,
        }}>
          No products available — check that `/v3/config/products` has been seeded.
        </div>
      )}

      {/* Bill 2026-05-08 lock — Opening Deposit, POD/IRA Beneficiary picker,
          and Debit Card Design previously lived as flat CollapsibleSections
          below the product grid. v0.14.23 strips them: per-product binding
          now happens INSIDE the AddToCartModal that opens on card click. */}

      {/* Bill 2026-05-13 — Screen 22/23 picker chain. */}
      <ProductCategoryPickerModal
        open={pickerStep === 'category'}
        onClose={() => setPickerStep('closed')}
        onPick={(key) => {
          setPickerCategoryKey(key);
          setPickerStep('tiles');
        }}
      />
      {pickerCategoryKey && (
        <ProductTilesPickerModal
          open={pickerStep === 'tiles'}
          categoryKey={pickerCategoryKey}
          businessTypes={businessTypes}
          personalTypes={personalTypes}
          applicantCustomerTypes={people
            .map((p) => p.customerType)
            .filter((t): t is string => !!t)}
          cartPeople={modalPeople}
          onClose={() => setPickerStep('closed')}
          onReturn={() => setPickerStep('category')}
          onPick={(productCode) => {
            setPickerStep('closed');
            setPickerCategoryKey(null);
            // NEW add — pre-generate the row-store instance id ("li-{uuid}").
            // POST /line-items ALWAYS creates a new line, so picking a code
            // already in the cart yields a SECOND independent instance.
            setModalTarget({ mode: 'add', productCode, instanceId: genLineItemId() });
          }}
        />
      )}

      <AddToCartModal
        // Remount per INSTANCE so the useState initializers re-seed from
        // `initial` on every open/edit (the modal never unmounts otherwise →
        // owners, features AND the v0.24.1 beneficiary re-hydration would show
        // the prior instance's values). Keyed on the line-item instance being
        // configured — two instances of the same code remount independently.
        key={modalTarget?.instanceId ?? 'closed'}
        open={modalTarget !== null && modalProduct !== null}
        product={modalProduct}
        people={modalPeople}
        features={modalFeatures}
        productFeatures={modalProductFeatures}
        pickerSources={modalPickerSources}
        featureNotes={modalFeatureNotes}
        cardTypes={modalCardTypes}
        onClose={() => setModalTarget(null)}
        onCommit={onCommitModal}
        {...(modalInitial ? { initial: modalInitial } : {})}
        {...(modalInstanceLabel ? { instanceLabel: modalInstanceLabel } : {})}
        reusableBeneficiaries={
          modalTarget ? collectReusableBeneficiaries(cart, modalTarget.instanceId) : []
        }
        {...(onAddPerson && !readOnly
          ? {
              onAddPerson: () => {
                // Leave the unsaved line and add the person; the officer comes back to
                // Products to add the account with them as joint owner.
                setModalTarget(null);
                onAddPerson();
              },
            }
          : {})}
      />

      {/* Teams-safe cart-line removal confirm (Bill 2026-07-17) — replaces the
          window.confirm() that silently no-ops in the Teams webview. */}
      {pendingRemoval && (
        <ModalShell
          open
          onClose={() => setPendingRemoval(null)}
          title="Remove product from cart?"
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setPendingRemoval(null)}>Cancel</Button>
              <Button variant="danger" onClick={confirmRemoval}>Remove</Button>
            </>
          }
        >
          <p style={{ margin: 0, lineHeight: 1.5 }}>
            Remove <strong>{pendingRemoval.label}</strong> from the cart? This is audit-logged.
          </p>
        </ModalShell>
      )}
    </section>
  );
}
